import React, { useEffect, useMemo, useState } from 'react';
import {
  MAIN_CATEGORY_MAP, buildDefaultMainScreenConfig, mergeSavedIntoConfig, configToSaveItems,
} from '../../lib/config/main-screen-config';
import { getMainScreenSettings, saveMainScreenSettings } from '../../lib/api/ranker-api';
import { updateKbStatusBoard } from '../../lib/api/kb-api';
import './MainScreenSettings.css';

const SCOPE_LABEL = { region: '지역', dong: '생활권', danji: '단지' };
const asItems = (res) => (Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []));

function MainScreenSettings() {
  const [config, setConfig] = useState(() => buildDefaultMainScreenConfig());
  const [savedMsg, setSavedMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMainScreenSettings()
      .then((res) => { if (alive) setConfig(mergeSavedIntoConfig(asItems(res))); })
      .catch(() => { /* 실패 시 기본값 유지 */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const rows = useMemo(
    () => config.map((c) => ({ ...c, meta: MAIN_CATEGORY_MAP[c.id] })).filter((r) => r.meta),
    [config],
  );

  const update = (id, patch) => setConfig((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const move = (index, dir) => {
    setConfig((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveMainScreenSettings(configToSaveItems(config));
      setSavedMsg('저장되었습니다. (메인화면 새로고침 시 반영)');
    } catch (e) {
      setSavedMsg('저장에 실패했습니다.');
    } finally {
      setLoading(false);
      setTimeout(() => setSavedMsg(''), 2500);
    }
  };

  const handleReset = () => {
    if (!window.confirm('메인화면 설정을 기본값으로 되돌리시겠습니까? (저장 버튼을 눌러야 반영됩니다)')) return;
    setConfig(buildDefaultMainScreenConfig());
    setSavedMsg('기본값으로 초기화했습니다. (저장하려면 저장 버튼)');
    setTimeout(() => setSavedMsg(''), 2500);
  };

  const [kbUpdating, setKbUpdating] = useState(false);
  const handleKbStatusUpdate = async () => {
    try {
      setKbUpdating(true);
      const res = await updateKbStatusBoard();
      setSavedMsg(res?.message || (res?.success ? 'KB 데이터셋 업데이트 완료' : 'KB 데이터셋 업데이트 실패'));
    } catch (e) {
      setSavedMsg('KB 데이터셋 업데이트 실패: ' + (e?.message || ''));
    } finally {
      setKbUpdating(false);
      setTimeout(() => setSavedMsg(''), 4000);
    }
  };

  return (
    <div className="mss-root">
      <div className="mss-head">
        <div>
          <h3 className="mss-title">메인화면설정</h3>
          <p className="mss-desc">
            메인 화면(지역별 TOP 랭킹)에 노출할 항목과 순서, 차트 표시 여부를 설정합니다.
            표는 항상 표시되며, 차트를 켜면 표 우측에 함께 나옵니다.
          </p>
        </div>
        <div className="mss-actions">
          {savedMsg && <span className="mss-saved">{savedMsg}</span>}
          <button type="button" className="mss-btn" onClick={handleKbStatusUpdate} disabled={kbUpdating} title="홈 상단 KB 주간/월간 지표를 최신으로 갱신">
            {kbUpdating ? 'KB 갱신 중...' : 'KB데이터셋 업데이트'}
          </button>
          <button type="button" className="mss-btn" onClick={handleReset} disabled={loading}>기본값</button>
          <button type="button" className="mss-btn primary" onClick={handleSave} disabled={loading}>{loading ? '처리 중...' : '저장'}</button>
        </div>
      </div>

      <div className="mss-table-wrap">
        <table className="mss-table">
          <thead>
            <tr>
              <th className="mss-c-order">순서</th>
              <th className="mss-c-on">표시</th>
              <th className="mss-c-name">항목</th>
              <th className="mss-c-type">차트</th>
              <th className="mss-c-scope">선택 범위(콤보)</th>
              <th className="mss-c-flag">특성</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={r.enabled ? '' : 'mss-off'}>
                <td className="mss-c-order">
                  <div className="mss-order-btns">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="위로">▲</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="아래로">▼</button>
                  </div>
                </td>
                <td className="mss-c-on">
                  <label className="mss-switch">
                    <input type="checkbox" checked={r.enabled} onChange={(e) => update(r.id, { enabled: e.target.checked })} />
                    <span />
                  </label>
                </td>
                <td className="mss-c-name">
                  <span className="mss-name" style={{ color: r.meta.color }}>{r.meta.title}</span>
                </td>
                <td className="mss-c-type">
                  <label className="mss-switch" title="차트 표시(표는 항상 표시)">
                    <input
                      type="checkbox"
                      checked={r.type === 'chart'}
                      onChange={(e) => update(r.id, { type: e.target.checked ? 'chart' : 'table' })}
                    />
                    <span />
                  </label>
                </td>
                <td className="mss-c-scope">
                  {(r.meta.scope || []).map((s) => <span key={s} className="mss-scope-badge">{SCOPE_LABEL[s] || s}</span>)}
                </td>
                <td className="mss-c-flag">
                  {r.meta.hasPeriod && <span className="mss-flag">기간</span>}
                  {r.meta.hasBudget && <span className="mss-flag">투자금선택</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mss-note">
        ※ 선택 범위(지역/생활권/단지)는 항목별 고정입니다. 지역→생활권→단지 순차 선택, 호버 상세정보, 투자금 억단위 세부선택,
        단지 클릭 시 네이버 이동은 메인화면에서 순차 적용됩니다.
      </p>
    </div>
  );
}

export default MainScreenSettings;
